import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

const imageKeys = [
    'section_hero_image',
    'section_sustain_image_one',
    'section_sustain_image_two',
    'section_visual_top_image',
    'section_visual_button_left_image',
    'section_visual_button_right_image',
    'section_contact_background_image'
];

const HomeCMS = () => {
    const { code } = useParams();
    const formRef = useRef(null);

    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        section_hero_heading_one: "",
        section_hero_heading_two: "",
        section_hero_description: "",
        section_hero_button_text: "",
        section_hero_button_link: "",
        section_sustain_title: "",
        section_sustain_description: "",
        section_sustain_button_text: "",
        section_visual_title: "",
        section_visual_description: "",
        section_visual_button_text: "",
        section_contact_subtitle: "",
        section_contact_button_text: ""
    });

    const [images, setImages] = useState({});
    const [previews, setPreviews] = useState({});
    const [activeTab, setActiveTab] = useState('section_hero');

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, key) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [key]: file }));
            setPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
        }
    };

    useEffect(() => {
        const fetchCMS = async () => {
            try {
                const res = await api.get(`/cms/${code}`);
                if (res.status === 200) {
                    const data = res.data.cms;

                    const previewObj = {};
                    imageKeys.forEach(key => {
                        if (data[key]) {
                            previewObj[key] = `${res.data.base_url}${data[key]}`;
                        }
                    });

                    setPreviews(previewObj);

                    const {
                        section_hero_heading_one,
                        section_hero_heading_two,
                        section_hero_description,
                        section_hero_button_text,
                        section_hero_button_link,
                        section_sustain_title,
                        section_sustain_description,
                        section_sustain_button_text,
                        section_visual_title,
                        section_visual_description,
                        section_visual_button_text,
                        section_contact_subtitle,
                        section_contact_button_text
                    } = data;

                    setFormData({
                        section_hero_heading_one,
                        section_hero_heading_two,
                        section_hero_description,
                        section_hero_button_text,
                        section_hero_button_link,
                        section_sustain_title,
                        section_sustain_description,
                        section_sustain_button_text,
                        section_visual_title,
                        section_visual_description,
                        section_visual_button_text,
                        section_contact_subtitle,
                        section_contact_button_text
                    });
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchCMS();

        if (formRef.current && window.$.fn.validate) {

            window.$.validator.addMethod(
                "ckeditorRequired",
                (value) =>
                    value.replace(/<[^>]*>/g, "").trim().length > 0,
                "This field is required"
            );

            window.$(formRef.current).validate({
                ignore: ".ck-editor *",
                rules: {
                    section_hero_heading_one: { required: true },
                    section_hero_heading_two: { required: true },
                    section_hero_description: { ckeditorRequired: false },
                    section_hero_button_text: { required: true },
                    section_hero_button_link: { required: true },

                    section_sustain_title: { required: true },
                    section_sustain_description: { ckeditorRequired: true },
                    section_sustain_button_text: { required: true },

                    section_visual_title: { required: true },
                    section_visual_description: { ckeditorRequired: true },
                    section_visual_button_text: { required: true },

                    section_contact_subtitle: { required: true },
                    section_contact_button_text: { required: true },
                },
                messages: {},
                errorElement: 'label',
                validClass: 'is-valid',
                errorClass: 'is-invalid text-danger',
                highlight: (element, errorClass, validClass) => {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: (element, errorClass, validClass) => {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [code]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => data.append(key, value));
            Object.entries(images).forEach(([key, file]) => data.append(key, file));

            try {
                setSubmitting(true);
                const res = await api.put(`/cms/${code}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showToast('success', res.data.message, 'Success');
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const renderImageInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label>{label}</label>
            <input type="file" className="form-control" onChange={e => handleImageChange(e, name)} />
            {previews[name] && <img src={previews[name]} alt="preview" width={100} className="mt-2" />}
        </div>
    );

    const renderTextInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label className="form-label">{label}</label>
            <input
                type="text"
                className="form-control"
                name={name}
                value={formData[name]}
                onChange={handleChange}
            />
        </div>
    );

    const renderTextareaInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label className="form-label">{label}</label>
            <CKEditor
                key={name}
                editor={ClassicEditor}
                config={{
                    toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', 'code', 'codeBlock',
                        '|', 'bulletedList', 'numberedList'
                    ]
                }}
                data={formData[name]}
                onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData(prev => ({ ...prev, [name]: data }));
                }}
            />
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'section_hero':
                return (
                    <>
                        {renderTextInput('Heading One', 'section_hero_heading_one')}
                        {renderTextInput('Heading Two', 'section_hero_heading_two')}
                        {renderTextareaInput('Description', 'section_hero_description')}
                        {renderTextInput('Button Text', 'section_hero_button_text')}
                        {renderTextInput('Button Link', 'section_hero_button_link')}
                        {renderImageInput('Hero Image', 'section_hero_image')}
                    </>
                );
            case 'section_sustain':
                return (
                    <>
                        {renderTextInput('Title', 'section_sustain_title')}
                        {renderTextareaInput('Description', 'section_sustain_description')}
                        {renderTextInput('Button Text', 'section_sustain_button_text')}
                        {renderImageInput('Image One', 'section_sustain_image_one')}
                        {renderImageInput('Image Two', 'section_sustain_image_two')}
                    </>
                );
            case 'section_visual':
                return (
                    <>
                        {renderTextInput('Title', 'section_visual_title')}
                        {renderTextareaInput('Description', 'section_visual_description')}
                        {renderTextInput('Button Text', 'section_visual_button_text')}
                        {renderImageInput('Image Top', 'section_visual_top_image')}
                        {renderImageInput('Button Image Left', 'section_visual_button_left_image')}
                        {renderImageInput('Button Image Right', 'section_visual_button_right_image')}
                    </>
                );
            case 'section_contacts':
                return (
                    <>
                        {renderTextInput('Sub Title', 'section_contact_subtitle')}
                        {renderTextInput('Button Text', 'section_contact_button_text')}
                        {renderImageInput('Background Image', 'section_contact_background_image')}
                    </>
                );


            default:
                return null;
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/cms/home`}>Home Page</Link>
                    </li>
                </ol>
            </div>

            <div className="card">
                <div className="card-header">
                    <h4 className="card-title">Home CMS</h4>
                </div>
                <div className="card-body">
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_hero' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_hero')}
                            >
                                Section Hero
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_sustain' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_sustain')}
                            >
                                Section Sustain
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_visual' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_visual')}
                            >
                                Section Visual
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_contacts' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_contacts')}
                            >
                                Section Contacts
                            </button>
                        </li>
                    </ul>
                    <form ref={formRef} noValidate>
                        <div className="row">{renderTabContent()}</div>
                        <div className="pt-3">
                            <button
                                type="button"
                                onClick={handleFormSubmit}
                                className="btn btn-sm btn-primary me-2"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <i className="spinner-border spinner-border-sm me-1"></i>
                                        Submit
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-floppy-disk me-1"></i>
                                        Submit
                                    </>
                                )}
                            </button>
                            <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                <i className="fas fa-times-circle"></i>&nbsp;Cancel
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default HomeCMS;
